// TELEMETRY-GATE: F1 satisfied at the init site (`src/utils/tracing.ts`
// returns early on `!isSaasTelemetryEnabled`, so `datadogRum.init` never
// runs). Calls below
// are no-ops when RUM is uninitialised, matching the project pattern for
// every other `datadogRum.*` callsite (analyticsScribeModel, performance
// tracking, error-boundary, startDurationVital).
import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';

/**
 * Forwards click events inside the PiP window to Datadog RUM.
 *
 * Datadog's `trackUserInteractions: true` registers a click listener on the
 * main `document`. The PiP window is rendered via `createPortal` into
 * `pipWindow.document.body`, so native click events fire on a different
 * document and never bubble to RUM's listener — the entire mini-mode
 * interaction surface is a telemetry blind spot without this bridge.
 *
 * Only the closest `[data-testid]` ancestor's testid, tag, and role are
 * forwarded. `aria-label` and text content are deliberately omitted —
 * `addAction`'s custom attributes transit unmasked (`defaultPrivacyLevel`
 * applies to auto-captured names/snapshots only), so a future dynamic
 * label (mic-picker device name, elapsed-time string) would leak. The
 * testid alone is sufficient for the SCR-3977 acceptance criterion.
 */
export function usePipRumBridge({ pipWindow }: { pipWindow: Window | null }): void {
  useEffect(() => {
    if (!pipWindow) return;

    const handleClick = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const elementWithTestid = target.closest('[data-testid]');
      if (!elementWithTestid) return;
      const testid = elementWithTestid.getAttribute('data-testid');
      if (!testid) return;

      datadogRum.addAction('pip_click', {
        testid,
        tag: elementWithTestid.tagName,
        role: elementWithTestid.getAttribute('role'),
      });
    };

    pipWindow.document.addEventListener('click', handleClick);

    return () => {
      pipWindow.document.removeEventListener('click', handleClick);
    };
  }, [pipWindow]);
}
